import { useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Collapse from "@mui/material/Collapse"
import useMediaQuery from "@mui/material/useMediaQuery"
import { NavLink as Link, useMatch } from "react-router"
import { styled, useTheme } from "@mui/material/styles"
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded"
import ClearRoundedIcon from "@mui/icons-material/ClearRounded"
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded"
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined"
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined"
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined"
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { useToolBarHeight } from "../../components/fullHeight"
import disableScroll from "disable-scroll"

const NavBox = styled(({ expanded, className, ...props }) => (
  <Box className={expanded ? `${className} expanded` : className} {...props} />
))(({ theme }) => {
  const height = useToolBarHeight()

  return {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    top: height,
    backgroundColor: theme.palette.grey[100],
    padding: `${theme.spacing(4)} 0 0 ${theme.spacing(2)}`,
    zIndex: 500,
    transition: theme.transitions.create("clip-path", {
      duration: theme.transitions.duration.navOut,
      easing: theme.transitions.easing.nav
    }),
    clipPath: "inset(100% 0 0 0)",
    "&.expanded": {
      clipPath: "inset(0)",
      transition: theme.transitions.create("clip-path", {
        duration: theme.transitions.duration.navIn,
        easing: theme.transitions.easing.nav
      })
    },
    [theme.breakpoints.up("sm")]: {
      position: "sticky",
      bottom: "initial",
      top: height,
      left: "initial",
      right: "initial",
      minWidth: theme.spacing(20),
      height: "fit-content",
      clipPath: "initial",
      padding: `${theme.spacing(6)} 0 ${theme.spacing(20)} ${theme.spacing(8)}`
    },
    [theme.breakpoints.up("md")]: {
      minWidth: theme.spacing(30)
    }
  }
})

const NavButton = styled(({ setExpanded, ...props }) => (
  <button onClick={() => setExpanded(expanded => !expanded)} {...props}>
    <ClearRoundedIcon fontSize="large" />
  </button>
))(({ theme, expanded }) => ({
  backgroundColor: "#d7f5ff",
  color: theme.palette.primary.main,
  cursor: "pointer",
  borderRadius: "50%",
  display: "inline-block",
  position: "fixed",
  padding: theme.spacing(1),
  border: 0,
  fontSize: "0.1rem",
  WebkitTapHighlightColor: "transparent",
  right: "50px",
  bottom: "50px",
  zIndex: 750,
  transform: expanded ? "scale(1.15)" : "initial",
  transition: theme.transitions.create("transform", {
    duration: expanded ? 700 : 300
  }),
  "& .MuiSvgIcon-root": {
    transform: expanded ? "rotate(0deg)" : "rotate(45deg)",
    transition: theme.transitions.create("transform", {
      duration: expanded ? 700 : 300
    })
  },
  [theme.breakpoints.up("sm")]: {
    display: "none"
  }
}))

const NavLink = styled(
  ({ children, className, setExpanded, component, ...others }) => (
    <Link
      {...others}
      onClick={() => setExpanded(false)}
      className={({ isActive }) =>
        isActive ? `${className} active` : className
      }
    >
      <span className="indicator" />
      <Button
        sx={{ textAlign: "left" }}
        startIcon={component}
        aria-label={children}
      >
        {children}
      </Button>
    </Link>
  )
)(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.grey[700],
  padding: `${theme.spacing(0.8)} 0`,
  display: "flex",
  ...theme.typography.button,
  "&:hover": {
    color: theme.palette.grey[500]
  },
  "& .indicator": {
    width: theme.spacing(0.3),
    backgroundColor: "transparent",
    marginRight: theme.spacing(2)
  },
  "&.active": {
    color: theme.palette.secondary.main,
    "& .indicator": {
      backgroundColor: theme.palette.secondary.main
    }
  }
}))

const SubNavButton = styled(({ startIcon, ...props }) => (
  <Button
    variant="text"
    sx={{ textAlign: "left" }}
    endIcon={<KeyboardArrowDownRoundedIcon />}
    startIcon={startIcon}
    {...props}
  />
))(({ theme, expanded }) => ({
  marginLeft: theme.spacing(2),
  "& .MuiButton-endIcon": {
    transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest
    })
  }
}))

const SubNavCollapse = styled(Collapse)(({ theme }) => ({
  "& .MuiCollapse-wrapperInner": {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column"
  }
}))

const SubNavLink = styled(({ children, setExpanded, className, ...others }) => (
  <Link
    onClick={() => setExpanded(false)}
    className={({ isActive }) => (isActive ? `${className} active` : className)}
    {...others}
    aria-label={children}
  >
    <span className="indicator" />
    {children}
  </Link>
))(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.grey[700],
  padding: `${theme.spacing(0.8)} 0`,
  display: "flex",
  transition: theme.transitions.create("color", {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.sharp
  }),
  ...theme.typography.body2,
  "&:hover": {
    color: theme.palette.grey[500]
  },
  "& .indicator": {
    width: theme.spacing(0.3),
    backgroundColor: "transparent",
    marginRight: theme.spacing(2)
  },
  "&.active": {
    color: theme.palette.secondary.main,
    "& .indicator": {
      backgroundColor: theme.palette.secondary.main
    }
  }
}))

function SubNav(props) {
  const [expanded, setExpanded] = useState(
    useMatch({ path: `/resources/${props.format}`, end: false }) ? true : false
  )

  return (
    <Box sx={{ my: 1.4, ...props.sx }}>
      <SubNavButton
        expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        startIcon={props.component}
      >
        {props.header}
      </SubNavButton>
      <SubNavCollapse in={expanded}>{props.children}</SubNavCollapse>
    </Box>
  )
}

export default function Nav() {
  const theme = useTheme()
  const up = useMediaQuery(theme.breakpoints.up("sm"))
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (expanded) {
      disableScroll.on()
    } else {
      disableScroll.off()
    }
  }, [expanded])

  useEffect(() => {
    if (up && expanded) {
      setExpanded(false)
    }
  }, [up, expanded, setExpanded])

  return (
    <>
      <NavBox expanded={Boolean(expanded)}>
        <NavLink
          setExpanded={setExpanded}
          to="exam-info"
          component={<InfoOutlinedIcon />}
        >
          Exam Information
        </NavLink>
        <SubNav
          header="Past Papers"
          format="past-papers"
          component={<LibraryBooksOutlinedIcon />}
        >
          <SubNavLink setExpanded={setExpanded} to="past-papers/A">
            Category A
          </SubNavLink>
          <SubNavLink setExpanded={setExpanded} to="past-papers/B">
            Category B
          </SubNavLink>
          <SubNavLink setExpanded={setExpanded} to="past-papers/C">
            Category C
          </SubNavLink>
        </SubNav>
        <SubNav
          header="Syllabus"
          format="syllabus"
          component={<MenuBookOutlinedIcon />}
        >
          <SubNavLink setExpanded={setExpanded} to="syllabus/A">
            Category A
          </SubNavLink>
          <SubNavLink setExpanded={setExpanded} to="syllabus/B">
            Category B
          </SubNavLink>
          <SubNavLink setExpanded={setExpanded} to="syllabus/C">
            Category C
          </SubNavLink>
        </SubNav>
        <NavLink
          setExpanded={setExpanded}
          to="prize"
          component={<EmojiEventsOutlinedIcon />}
        >
          Prizes
        </NavLink>
        <NavLink
          setExpanded={setExpanded}
          to="team"
          component={<GroupsOutlinedIcon />}
        >
          The Team
        </NavLink>
        <NavLink
          setExpanded={setExpanded}
          to="privacy"
          component={<AdminPanelSettingsOutlinedIcon />}
        >
          Privacy Policy
        </NavLink>
        <NavLink
          setExpanded={setExpanded}
          to="refund"
          component={<CurrencyRupeeOutlinedIcon />}
        >
          Refund Policy
        </NavLink>
        <NavLink
          setExpanded={setExpanded}
          to="terms"
          component={<BalanceRoundedIcon />}
        >
          Terms and Conditions
        </NavLink>
      </NavBox>
      <NavButton
        expanded={expanded}
        setExpanded={setExpanded}
        role="button"
        aria-label="Expand Resources"
      />
    </>
  )
}
